/**
 * Portfolio data workflows: a deal's normalized tables (`/api/portfolio-data`).
 *
 * A normalizer turns a client's presentation-shaped workbooks into long,
 * cited tables (one row per house, year and line item) with a manifest of how
 * each table was made. The backend serves them for the caller's own deal
 * rooms; this is the only place that calls it.
 */

import { apiFetch } from "../utils/apiClient";

export type Basis = "actual" | "allocated" | "derived";

export interface PortfolioFact {
	house_id: string;
	plat_id: string;
	year: number;
	line_item: string;
	value: number | null;
	unit: string;
	basis: Basis;
	source_label: string;
	source_file: string;
	sheet: string;
	cell: string;
	formula: string;
}

export interface PortfolioPlatFact {
	plat_id: string;
	plat_name: string;
	year: number;
	line_item: string;
	value: number | null;
	unit: string;
	basis: Basis;
	house_count: number | null;
	source_label: string;
	source_file: string;
	sheet: string;
	cell: string;
	formula: string;
}

export interface PortfolioLineItem {
	scope: "house" | "plat";
	line_item: string;
	label_pattern: string;
	unit: string;
	basis: Basis;
	definition: string;
}

export type PortfolioHouse = Record<string, string | number | null> & {
	house_id: string;
	plat_id: string;
	plat_name: string;
};

export interface PortfolioLoan {
	plat_id: string;
	plat_name: string;
	year: number;
	bank: string;
	house_count: number | null;
	loan_balance: number | null;
	interest_paid: number | null;
	principal_paid: number | null;
	interest_rates_entered: string;
	allocation: string;
}

export interface PortfolioComponent {
	house_id: string;
	component: string;
	installed_year: number | null;
	age_years: number | null;
	life_years_low: number | null;
	life_years_high: number | null;
	replacement_cost_low: number | null;
	replacement_cost_high: number | null;
	years_past_life: number | null;
	rule_text: string;
	source_file: string;
	sheet: string;
	year_cell: string;
	age_cell: string;
}

export interface BriefSheet {
	source: string;
	sheet: string;
	columns: number;
	arrived: "yes" | "part" | "no";
	to: string[];
	note: string;
}

export interface ArrivedFile {
	file: string;
	shape: string;
	issue: string;
}

export interface MappingFlow {
	from: string | null;
	from_detail: string;
	to: string;
	rows: number;
	change: string;
}

export interface ReviewItem {
	id: string;
	/** A finding about the portfolio, or a problem in the data sent. */
	area?: "portfolio" | "data";
	kind: "risk" | "conflict" | "unusable" | "empty" | "missing" | string;
	title: string;
	detail: string;
	plat_id?: string | null;
	cell?: string;
	houses?: string[];
	/** A question the person can answer by picking one of these. */
	question?: string;
	choices?: string[];
	/** The label of an upload the item asks for. */
	upload?: string;
}

export interface ReviewAnswer {
	status: "known" | "answered";
	answer: string | null;
	by: string;
	at: string;
}

export type ReviewState = Record<string, ReviewAnswer>;

/** One column of the client's property export, as a house row carries it. */
export interface PropertyField {
	/** The column's key in house.csv (or owner.csv for Owner Master). */
	key: string;
	/** The header as exported. */
	header: string;
	/** Its column letter in the export sheet. */
	column: string;
	/** The brief sheet it belongs to. */
	group: "Property Master" | "Property Features" | "Capital Master" | "Owner Master";
	/** Whether any house has a value (other than 0) in it. */
	filled: boolean;
}

export interface PropertySheetSpec {
	file: string;
	sheet: string;
	fields: PropertyField[];
	/** Capital components in export order, with the rule in their header. */
	components: { key: string; rule: string }[];
}

/** One input a value of an added column was made of. */
export interface AddedColumnInput {
	name: string;
	value: number | string | null;
	/** A fact's basis, or "house_list" for a field of the house list. */
	basis: Basis | "house_list" | null;
	source_file?: string;
	sheet?: string;
	cell?: string;
}

export interface AddedColumnValue {
	house_id: string;
	plat_id: string;
	year: number;
	value: number | null;
	/** Why the value is blank: an input missing, or a division by zero. */
	note: string | null;
	inputs: AddedColumnInput[];
}

/**
 * A column someone added from the Analyst rail: a formula over the tables,
 * worked out by the server for every house each time the page loads.
 */
export interface AddedColumn {
	id: string;
	label: string;
	formula: string;
	definition: string;
	unit: string;
	kind: "money" | "ratio" | "pct" | "number";
	decimals: number;
	/** What a plat's row shows. */
	aggregate: "mean" | "sum" | "none";
	added_by: string;
	added_at: string;
	values: AddedColumnValue[];
	/** Set when the tables no longer have one of the formula's names. */
	error: string | null;
}

export interface PortfolioManifest {
	built_at: string;
	years: number[];
	house_count: number;
	plats: {
		plat_id: string;
		plat_name: string;
		house_count: number;
		bank: string;
		interest_rates_entered?: string;
	}[];
	source_files: string[];
	brief_sheets: BriefSheet[];
	arrived: ArrivedFile[];
	flows: MappingFlow[];
	property_sheet?: PropertySheetSpec;
	reconciliation: { checked: number };
	review: ReviewItem[];
}

export interface PortfolioSourceFile {
	name: string;
	attachment_id: number | null;
}

export interface Portfolio {
	deal_name: string;
	project_id: string | null;
	/** The deal's folder in the Deal Room, where files are uploaded. */
	deal_room_id?: number | null;
	manifest: PortfolioManifest;
	tables: {
		house: PortfolioHouse[];
		fact: PortfolioFact[];
		plat_fact: PortfolioPlatFact[];
		line_item: PortfolioLineItem[];
		loan: PortfolioLoan[];
		owner: Record<string, string | number | null>[];
		capital_component: PortfolioComponent[];
	};
	notes: string;
	review_state?: ReviewState;
	/** Columns added from the Analyst rail; absent from an older backend. */
	columns?: AddedColumn[];
	source_files: PortfolioSourceFile[];
}

export interface PortfolioSummary {
	deal_name: string;
	project_id: string | null;
	built_at: string | null;
	years: number[];
	house_count: number;
	plat_count: number;
	review_count: number;
}

export class PortfolioNotFoundError extends Error {}

const BASE = "/api/portfolio-data";

async function readJson<T>(endpoint: string): Promise<T> {
	const response = await apiFetch(endpoint);
	if (response.status === 404) {
		throw new PortfolioNotFoundError("These portfolio tables were not found.");
	}
	if (!response.ok) {
		throw new Error(`Could not load the portfolio data (${response.status}).`);
	}
	return (await response.json()) as T;
}

export const listPortfolios = (): Promise<PortfolioSummary[]> =>
	readJson<PortfolioSummary[]>(BASE);

export const getPortfolio = (dealName: string): Promise<Portfolio> =>
	readJson<Portfolio>(`${BASE}/${encodeURIComponent(dealName)}`);

export const portfolioHref = (dealName: string): string =>
	`/workflows/portfolio-data/${encodeURIComponent(dealName)}`;

/** Mark an item known, answer its question, or reopen it ("open"). */
export async function updateReview(
	dealName: string,
	itemId: string,
	status: "open" | "known" | "answered",
	answer: string | null = null,
): Promise<ReviewState> {
	const response = await apiFetch(
		`${BASE}/${encodeURIComponent(dealName)}/review/${encodeURIComponent(itemId)}`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ status, answer }),
		},
	);
	if (!response.ok) throw new Error(`Could not save that (${response.status}).`);
	return ((await response.json()) as { review_state: ReviewState }).review_state;
}

/** Remove a column that was added from the Analyst rail. */
export async function removeColumn(dealName: string, columnId: string): Promise<AddedColumn[]> {
	const response = await apiFetch(
		`${BASE}/${encodeURIComponent(dealName)}/columns/${encodeURIComponent(columnId)}`,
		{ method: "DELETE" },
	);
	if (!response.ok) throw new Error(`Could not remove that column (${response.status}).`);
	return ((await response.json()) as { columns: AddedColumn[] }).columns;
}
